import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { salesLogService } from '../../services/sales-log.service';
import { operatorService } from '../../services/operator.service';
import { userService } from '../../services/user.service';
import type { LogType, Operator, SalesLog, SalesLogInput } from '../../types/crm';
import type { User } from '../../types';
import Loading from '../../components/Loading';

const LOG_TYPES: LogType[] = ['visit', 'call', 'sample_drop', 'follow_up', 'demo', 'other'];

const createSalesLogSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  log_type: z.enum(['visit', 'call', 'sample_drop', 'follow_up', 'demo', 'other']),
  log_date: z.string().min(1, 'Log date is required'),
  operator_id: z.string().optional(),
  duration_minutes: z.string().optional(),
  outcome: z.string().optional(),
  notes: z.string().optional(),
});

type CreateSalesLogFormData = z.infer<typeof createSalesLogSchema>;

const titleize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const logTypeBadge = (logType: LogType): string => {
  switch (logType) {
    case 'visit':
      return 'bg-blue-100 text-blue-800';
    case 'call':
      return 'bg-indigo-100 text-indigo-800';
    case 'sample_drop':
      return 'bg-purple-100 text-purple-800';
    case 'follow_up':
      return 'bg-yellow-100 text-yellow-800';
    case 'demo':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

export const SalesLogs = () => {
  const [salesLogs, setSalesLogs] = useState<SalesLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [logTypeFilter, setLogTypeFilter] = useState<LogType | ''>('');
  const [operatorFilter, setOperatorFilter] = useState<number | ''>('');
  const [userFilter, setUserFilter] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateSalesLogFormData>({
    resolver: zodResolver(createSalesLogSchema),
    defaultValues: {
      log_type: 'visit',
      log_date: todayIso(),
    },
  });

  const loadSalesLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (logTypeFilter) {
        params['q[log_type_eq]'] = LOG_TYPES.indexOf(logTypeFilter);
      }
      if (operatorFilter) params['q[operator_id_eq]'] = operatorFilter;
      if (userFilter) params['q[user_id_eq]'] = userFilter;
      if (dateFrom) params['q[log_date_gteq]'] = dateFrom;
      if (dateTo) params['q[log_date_lteq]'] = dateTo;
      const result = await salesLogService.getAll(params);
      setSalesLogs(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [logTypeFilter, operatorFilter, userFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadSalesLogs();
  }, [loadSalesLogs]);

  useEffect(() => {
    operatorService
      .getAll({ per_page: 200 })
      .then((res) => setOperators(res.data))
      .catch((err) => toast.error(getErrorMessage(err)));
    userService
      .getAllUsers()
      .then((res) => setUsers(res))
      .catch(() => {
        // Silently ignore for non-admin reps — user filter just stays empty.
      });
  }, []);

  const onCreate = async (data: CreateSalesLogFormData) => {
    setIsCreating(true);
    try {
      const input: SalesLogInput = {
        subject: data.subject,
        log_type: data.log_type,
        log_date: data.log_date,
        operator_id: data.operator_id ? parseInt(data.operator_id, 10) : null,
        duration_minutes: data.duration_minutes ? parseInt(data.duration_minutes, 10) : null,
        outcome: data.outcome?.trim() ? data.outcome.trim() : null,
        notes: data.notes?.trim() ? data.notes.trim() : null,
      };
      await salesLogService.create(input);
      toast.success('Sales log created');
      setShowCreateModal(false);
      reset({ log_type: 'visit', log_date: todayIso() });
      loadSalesLogs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales logs</h1>
          <p className="text-sm text-gray-600 mt-1">Daily sales activity logged by the field reps.</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Log activity
        </button>
      </div>

      <div className="card p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="label">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="label">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="label">Type</label>
          <select
            value={logTypeFilter}
            onChange={(e) => setLogTypeFilter(e.target.value as LogType | '')}
            className="input"
          >
            <option value="">All types</option>
            {LOG_TYPES.map((s) => (
              <option key={s} value={s}>{titleize(s)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Operator</label>
          <select
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value ? Number(e.target.value) : '')}
            className="input"
          >
            <option value="">All operators</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>{op.name}</option>
            ))}
          </select>
        </div>
        {users.length > 0 && (
          <div>
            <label className="label">Rep</label>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value ? Number(e.target.value) : '')}
              className="input"
            >
              <option value="">All reps</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8"><Loading /></div>
        ) : salesLogs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No sales logs found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rep</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {salesLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{log.log_date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                      <Link to={`/crm/sales-logs/${log.id}`}>{log.subject}</Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${logTypeBadge(log.log_type)}`}>
                        {titleize(log.log_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{log.operator?.name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{log.user.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      {log.duration_minutes ? `${log.duration_minutes} min` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)" onClick={() => setShowCreateModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreate)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Log activity</h3>
                    <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="subject" className="label">Subject</label>
                      <input {...register('subject')} id="subject" type="text" className="input" />
                      {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="log_type" className="label">Type</label>
                        <select {...register('log_type')} id="log_type" className="input">
                          {LOG_TYPES.map((s) => (
                            <option key={s} value={s}>{titleize(s)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="log_date" className="label">Date</label>
                        <input {...register('log_date')} id="log_date" type="date" className="input" />
                        {errors.log_date && <p className="mt-1 text-sm text-red-600">{errors.log_date.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="operator_id" className="label">Operator (optional)</label>
                      <select {...register('operator_id')} id="operator_id" className="input">
                        <option value="">— none —</option>
                        {operators.map((op) => (
                          <option key={op.id} value={op.id}>{op.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="duration_minutes" className="label">Duration (min)</label>
                        <input
                          {...register('duration_minutes')}
                          id="duration_minutes"
                          type="number"
                          inputMode="numeric"
                          className="input"
                        />
                      </div>
                      <div>
                        <label htmlFor="outcome" className="label">Outcome</label>
                        <input {...register('outcome')} id="outcome" type="text" className="input" />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="notes" className="label">Notes</label>
                      <textarea {...register('notes')} id="notes" rows={3} className="input" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isCreating} className="btn btn-primary">
                    {isCreating ? 'Saving...' : 'Save log'}
                  </button>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesLogs;
